import type { HubDbPoolHolderSnapshot } from "./slowRequestBanner";
import {
  displaySlowRequestPath,
  friendlySlowRequestLabel,
  slowRequestElapsedSeconds,
} from "./slowRequestBanner";

/** Auto-report once per page per 30 minutes (paired with server dedupe). */
export const SLOW_HUB_SUPPORT_DEDUPE_WINDOW_MS = 30 * 60_000;

/** Start auto-report after this many seconds of sustained slowness. */
export const SLOW_HUB_AUTO_REPORT_ELAPSED_SECONDS = 30;

/** Require at least this many in-flight slow requests before auto-report. */
export const SLOW_HUB_AUTO_REPORT_MIN_REQUESTS = 2;

export interface SlowHubSupportSlowRow {
  path: string;
  startedAtMs: number;
  method: string;
}

/** Minimal view of an origin reachability probe (see slowRequestNetworkDiagnostics). */
export interface SlowHubOriginProbe {
  outcome: "response" | "timeout" | "error";
  status: number | null;
}

/**
 * Browser-side reachability evidence collected at report time.
 * `liveOriginProbe` is null when the live stream shares the document origin.
 */
export interface SlowHubNetworkEvidence {
  mainOriginProbe: SlowHubOriginProbe | null;
  liveOriginProbe: SlowHubOriginProbe | null;
  online: boolean | null;
}

export interface SlowHubSupportTicketContext {
  slowRows: readonly SlowHubSupportSlowRow[];
  holderSnapshot: HubDbPoolHolderSnapshot | null;
  holderStreamStatus: string;
  nowMs: number;
  trigger: "auto" | "manual";
  /** Optional; when absent the classifier cannot detect client network loss. */
  network?: SlowHubNetworkEvidence | null;
}

export type SlowHubBacklogType =
  | "client_network_unreachable"
  | "megaventory_queue_backlog"
  | "database_queue_backlog"
  | "tab_or_other_queue_backlog";

/**
 * True when this browser could not get any HTTP response from the hub origin
 * (and from the live-stream origin when it is separate). No status code at all
 * means the request never reached Apache or the API, so it is not a hub queue
 * backlog and should not be reported as one.
 */
export function isClientNetworkUnreachable(network: SlowHubNetworkEvidence | null | undefined): boolean {
  if (network == null) return false;
  if (network.online === false) return true;
  const main = network.mainOriginProbe;
  if (main == null || main.outcome === "response") return false;
  const live = network.liveOriginProbe;
  if (live != null && live.outcome === "response") return false;
  return true;
}

export interface SlowHubQueueReport {
  backlogTypes: SlowHubBacklogType[];
  database: {
    inUse: number | null;
    max: number | null;
    holders: number;
    untrackedInUse: number;
    waiters: number;
  };
  megaventory: { inUse: number; max: number | null; holders: number; waiters: number };
  tabOrOther: { slowRequests: number };
}

export function buildSlowHubQueueReport(context: SlowHubSupportTicketContext): SlowHubQueueReport {
  const queues = context.holderSnapshot?.queues ?? [];
  const databaseQueue = queues.find((queue) => queue.id === "supabase-query");
  const megaventoryQueue = queues.find((queue) => queue.id === "megaventory-http");
  const databaseWaiters = databaseQueue?.waiters.length ?? context.holderSnapshot?.waiters.length ?? 0;
  const megaventoryWaiters = megaventoryQueue?.waiters.length ?? 0;
  const backlogTypes: SlowHubBacklogType[] = [];
  const clientNetworkUnreachable = isClientNetworkUnreachable(context.network);
  if (clientNetworkUnreachable) {
    backlogTypes.push("client_network_unreachable");
  }
  if (megaventoryWaiters > 0) {
    backlogTypes.push("megaventory_queue_backlog");
  }
  if (databaseWaiters > 0) {
    backlogTypes.push("database_queue_backlog");
  }
  if (
    context.slowRows.length > 0 &&
    databaseWaiters === 0 &&
    megaventoryWaiters === 0 &&
    !clientNetworkUnreachable
  ) {
    backlogTypes.push("tab_or_other_queue_backlog");
  }
  return {
    backlogTypes,
    database: {
      inUse: databaseQueue?.inUse ??
        (context.holderSnapshot?.pool == null
          ? null
          : Math.max(0, context.holderSnapshot.pool.totalCount - context.holderSnapshot.pool.idleCount)),
      max: databaseQueue?.max ?? context.holderSnapshot?.pool?.max ?? null,
      holders: databaseQueue?.holders.length ?? context.holderSnapshot?.holders.length ?? 0,
      untrackedInUse: databaseQueue?.untrackedInUse ?? 0,
      waiters: databaseWaiters,
    },
    megaventory: {
      inUse: megaventoryQueue?.inUse ?? 0,
      max: megaventoryQueue?.max ?? null,
      holders: megaventoryQueue?.holders.length ?? 0,
      waiters: megaventoryWaiters,
    },
    tabOrOther: { slowRequests: context.slowRows.length },
  };
}

function backlogTypeLabel(type: SlowHubBacklogType): string {
  if (type === "client_network_unreachable") {
    return "client network unreachable (this browser got no HTTP response from the hub; not a hub queue backlog)";
  }
  if (type === "megaventory_queue_backlog") return "Megaventory queue backlog";
  if (type === "database_queue_backlog") return "database queue backlog";
  return "tab or other queue backlog";
}

function describeProbe(probe: SlowHubOriginProbe | null): string {
  if (probe == null) return "not probed";
  if (probe.outcome === "response") return `HTTP ${probe.status ?? "?"}`;
  return probe.outcome === "timeout" ? "timed out (no response)" : "network error (no response)";
}

function describeNetworkEvidence(network: SlowHubNetworkEvidence | null | undefined): string {
  if (network == null) return "";
  const online = network.online == null ? "" : `, browser online=${String(network.online)}`;
  return (
    `Network evidence: hub origin /healthz ${describeProbe(network.mainOriginProbe)}; ` +
    `live-stream origin /healthz ${describeProbe(network.liveOriginProbe)}${online}.\n`
  );
}

export function slowHubSupportDedupeKey(pagePath: string, nowMs: number): string {
  const bucket = Math.floor(nowMs / SLOW_HUB_SUPPORT_DEDUPE_WINDOW_MS);
  return `hub-slow:${pagePath}:${bucket}`;
}

const HUB_DEV_HOSTNAMES: ReadonlySet<string> = new Set(["localhost", "127.0.0.1", "[::1]", "0.0.0.0"]);

/**
 * True when this page is a local Hub Dev stack (dev build, Vite dev server, or a
 * localhost / *.local origin). Dev stacks share the production ticket table and
 * routinely restart the API under tsx watch, so they must never auto-file
 * slow-hub tickets. Manual reports are unaffected.
 */
export function isHubDevInstance(input: {
  clientBuildVersion: string;
  viteDev: boolean;
  hostname: string;
}): boolean {
  if (input.viteDev) return true;
  if (input.clientBuildVersion.trim() === "dev") return true;
  const hostname = input.hostname.toLowerCase();
  if (HUB_DEV_HOSTNAMES.has(hostname)) return true;
  return hostname.endsWith(".localhost") || hostname.endsWith(".local");
}

export function shouldAutoReportSlowHub(
  slowRowCount: number,
  slowElapsedSeconds: number | null,
): boolean {
  if (slowElapsedSeconds == null) {
    return false;
  }
  return (
    slowRowCount >= SLOW_HUB_AUTO_REPORT_MIN_REQUESTS &&
    slowElapsedSeconds >= SLOW_HUB_AUTO_REPORT_ELAPSED_SECONDS
  );
}

export function buildSlowHubSupportTicketPrefill(context: SlowHubSupportTicketContext): {
  actualBehavior: string;
  expectedBehavior: string;
} {
  const bannerRows = context.slowRows.map((row) => ({
    pathDisplay: displaySlowRequestPath(row.path),
    elapsedSeconds: slowRequestElapsedSeconds(row.startedAtMs, context.nowMs),
    friendlyLabel: friendlySlowRequestLabel(row.path),
    method: row.method,
  }));
  const maxElapsed = bannerRows.reduce((max, row) => Math.max(max, row.elapsedSeconds), 0);
  const requestCount = bannerRows.length;
  const pathLines = bannerRows
    .slice(0, 12)
    .map((row) => {
      const label = row.friendlyLabel ? `${row.friendlyLabel}: ` : "";
      return `- ${label}${row.method} ${row.pathDisplay} (${row.elapsedSeconds}s)`;
    })
    .join("\n");
  const more =
    bannerRows.length > 12 ? `\n- …and ${bannerRows.length - 12} more in-flight requests` : "";
  const triggerLabel = context.trigger === "auto" ? "Auto-reported" : "User-reported";
  const queueReport = buildSlowHubQueueReport(context);
  const classification = queueReport.backlogTypes.map(backlogTypeLabel).join(", ");
  const databaseCapacity =
    queueReport.database.inUse == null || queueReport.database.max == null
      ? "unknown"
      : `${queueReport.database.inUse}/${queueReport.database.max}`;
  const megaventoryCapacity =
    queueReport.megaventory.max == null
      ? `${queueReport.megaventory.inUse}/unknown`
      : `${queueReport.megaventory.inUse}/${queueReport.megaventory.max}`;
  const queueLines =
    `Slowdown class: ${classification || "unknown"}.\n` +
    `Queue evidence: database ${databaseCapacity}, ${queueReport.database.holders} tracked holders, ` +
    `${queueReport.database.untrackedInUse} untracked occupied lanes, ${queueReport.database.waiters} waiters; ` +
    `Megaventory ${megaventoryCapacity}, ` +
    `${queueReport.megaventory.waiters} waiters; this tab ${queueReport.tabOrOther.slowRequests} slow requests.\n`;
  const networkLine = describeNetworkEvidence(context.network);
  return {
    actualBehavior: `${queueLines}${networkLine}${triggerLabel}: ${requestCount} hub ${
      requestCount === 1 ? "request is" : "requests are"
    } taking longer than usual (${maxElapsed}s so far).\n${pathLines}${more}`,
    expectedBehavior:
      "Hub pages and API calls should respond within a few seconds under normal load.",
  };
}
